import { useState, useRef, useEffect, useCallback, type ChangeEvent } from 'react'
import { Button, Dropdown, Typography, theme } from 'antd'
import {
    SendOutlined,
    PaperClipOutlined,
    FileImageOutlined,
    VideoCameraOutlined,
    FileOutlined,
    CloseOutlined,
    EditOutlined,
    SmileOutlined,
} from '@ant-design/icons'
import type { MenuProps } from 'antd'
import type { Editor } from '@tiptap/react'
import { observer } from 'mobx-react-lite'
import { HEIC_ACCEPT, heicToJpeg } from '@/Ship/Helpers/heicPhoto'
import { useNotification } from '@/Ship/Hooks/useNotification'
import { useStore } from '../../Stores'
import { truncateText } from '../../Helpers/truncateText'
import { stripHtml } from '../../Helpers/stripHtml'
import { CHAT_ATTACHMENTS_ENABLED, CHAT_TYPING_ENABLED } from '../../Helpers/chatFeatures'
import { SendTypingTask } from '../../Tasks/SendTypingTask'
import type { User } from '../../Contracts'
import EmojiPicker, { EmojiPanel } from './EmojiPicker'
import TiptapEditor from './TiptapEditor'

const { Text } = Typography

/** Re-signal "typing" at most this often; receivers hide after 6s of silence */
const TYPING_KEEPALIVE_MS = 4000

const MessageInput = observer(() => {
    const { uiStore, messageStore, userStore, chatStore } = useStore()
    const { showError } = useNotification()
    const { token } = theme.useToken()
    const [hasContent, setHasContent] = useState(false)
    const [isEmojiOpen, setIsEmojiOpen] = useState(false)
    const editorRef = useRef<Editor | null>(null)
    const fileInputRef = useRef<HTMLInputElement>(null)
    const pendingAttachType = useRef<'photo' | 'video' | 'file'>('file')
    const lastTypingSentAt = useRef(0)

    const chatId = uiStore.activeChatId
    const replyToId = uiStore.replyToMessageId
    const editingId = uiStore.editingMessageId

    // Reply preview data
    const repliedMessage = replyToId ? messageStore.getMessageById(replyToId) : null
    const repliedSender = repliedMessage ? userStore.getById(repliedMessage.senderId) : null

    // Editing data
    const editingMessage = editingId ? messageStore.getMessageById(editingId) : null

    // Participants for @mentions
    const activeChat = chatStore.activeChat
    const mentionableUsers: User[] = activeChat
        ? (activeChat.participantIds
              .filter((id) => id !== userStore.currentUserId)
              .map((id) => userStore.getById(id))
              .filter(Boolean) as User[])
        : []

    // Save draft for old chat, restore draft for new chat
    const prevChatIdRef = useRef(chatId)
    useEffect(() => {
        const editor = editorRef.current
        if (!editor) return
        const prevId = prevChatIdRef.current

        if (prevId && prevId !== chatId) {
            const text = editor.getText().trim()
            uiStore.setDraft(prevId, text ? editor.getHTML() : '')
        }

        const draft = chatId ? uiStore.getDraft(chatId) : undefined
        if (draft) {
            editor.commands.setContent(draft)
            setHasContent(!!editor.getText().trim())
        } else {
            editor.commands.clearContent()
            setHasContent(false)
        }

        prevChatIdRef.current = chatId
        // Новий чат — тротл сигналу «друкую» починається з нуля
        lastTypingSentAt.current = 0
    }, [chatId, uiStore])

    const pending = uiStore.pendingAttachment
    const canSend = (hasContent || !!pending) && !messageStore.isSending

    const handleSend = async () => {
        const editor = editorRef.current
        if (!editor || !chatId || messageStore.isSending) return

        const plainText = editor.getText().trim()
        if (!plainText && !pending) return

        if (editingId) {
            if (plainText) {
                const html = editor.getHTML()
                await messageStore.editMessage(editingId, html)
            }
            uiStore.clearEditing()
        } else {
            const content = plainText ? editor.getHTML() : ''
            const attachments = pending ? [pending.file] : undefined
            const sent = await messageStore.sendMessage(chatId, content, attachments, replyToId ?? undefined)
            if (!sent) {
                // Upload/send failed — keep the draft and attachment for a retry
                showError('Не вдалося надіслати', messageStore.error ?? undefined)
                return
            }
            // The sent message carries the server url, the local preview blob is free
            uiStore.clearPendingAttachment()
            uiStore.clearReply()
        }
        editor.commands.clearContent()
        setHasContent(false)
        if (chatId) uiStore.clearDraft(chatId)
        editor.commands.focus()
    }

    const handleCancel = () => {
        if (editingId) {
            uiStore.clearEditing()
            editorRef.current?.commands.clearContent()
            setHasContent(false)
        } else if (pending) {
            uiStore.clearPendingAttachment()
        }
    }

    const handleEmojiSelect = useCallback((emoji: string) => {
        editorRef.current?.commands.insertContent(emoji)
        editorRef.current?.commands.focus()
    }, [])

    // Тротльований «друкую»: перший символ шле одразу, далі keep-alive кожні
    // 4с, поки триває набір. Індикатор косметичний — помилки/429 мовчки
    // ковтаються. При редагуванні не сигналимо (нового повідомлення не буде).
    const handleContentChange = useCallback(
        (has: boolean) => {
            setHasContent(has)
            if (!has || !CHAT_TYPING_ENABLED || !chatId || editingId) return

            const now = Date.now()
            if (now - lastTypingSentAt.current < TYPING_KEEPALIVE_MS) return
            lastTypingSentAt.current = now
            void SendTypingTask(chatId).catch(() => {})
        },
        [chatId, editingId]
    )

    const acceptMap: Record<string, string> = {
        photo: `image/*,${HEIC_ACCEPT}`,
        video: 'video/*',
        file: '*/*',
    }

    const openFilePicker = (type: 'photo' | 'video' | 'file') => {
        if (!chatId || !fileInputRef.current) return
        pendingAttachType.current = type
        fileInputRef.current.accept = acceptMap[type] ?? '*/*'
        fileInputRef.current.value = ''
        fileInputRef.current.click()
    }

    const handleFileSelected = useCallback(
        async (e: ChangeEvent<HTMLInputElement>) => {
            const selected = e.target.files?.[0]
            if (!selected || !chatId) return
            const type = pendingAttachType.current
            let file = selected
            // «Фото» з iPhone (HEIC) → JPEG, інакше його не побачать у Chrome і Windows.
            // «Файл» надсилаємо як є — це документ, а не фото для перегляду
            if (type === 'photo') {
                try {
                    file = await heicToJpeg(selected)
                } catch (error) {
                    showError('Не вдалося додати фото', error instanceof Error ? error.message : undefined)
                    return
                }
            }
            uiStore.setPendingAttachment(type, file)
            editorRef.current?.commands.focus()
        },
        [chatId, uiStore, showError]
    )

    const attachMenuItems: MenuProps['items'] = [
        { key: 'photo', label: 'Фото', icon: <FileImageOutlined /> },
        { key: 'video', label: 'Відео', icon: <VideoCameraOutlined /> },
        { key: 'file', label: 'Файл', icon: <FileOutlined /> },
    ]

    const firstFile = repliedMessage?.files?.[0]
    const replyPreview = repliedMessage
        ? firstFile
            ? firstFile.type === 'photo'
                ? 'Фото'
                : firstFile.type === 'video'
                  ? ' Відео'
                  : `${firstFile.name}`
            : truncateText(stripHtml(repliedMessage.content), 50)
        : ''

    // Edit mode: initial content for the editor
    const editInitialContent = editingMessage && !editingMessage.files?.length ? editingMessage.content : undefined

    if (!chatId) return null

    return (
        <div
            style={{
                borderTop: `1px solid ${token.colorBorderSecondary}`,
                background: token.colorBgContainer,
            }}
        >
            {/* Edit preview bar */}
            {editingMessage && (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '8px 16px',
                        borderBottom: `1px solid ${token.colorBorderSecondary}`,
                    }}
                >
                    <EditOutlined style={{ color: token.colorPrimary, fontSize: 14 }} />
                    <div
                        style={{
                            flex: 1,
                            borderLeft: `3px solid ${token.colorPrimary}`,
                            paddingLeft: 8,
                        }}
                    >
                        <Text strong style={{ fontSize: 12, color: token.colorPrimary, display: 'block' }}>
                            Редагування
                        </Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {truncateText(stripHtml(editingMessage.content), 50)}
                        </Text>
                    </div>
                    <Button
                        type="text"
                        size="small"
                        icon={<CloseOutlined />}
                        onClick={() => {
                            uiStore.clearEditing()
                            editorRef.current?.commands.clearContent()
                            setHasContent(false)
                        }}
                        style={{ color: token.colorTextSecondary }}
                    />
                </div>
            )}

            {/* Reply preview bar */}
            {!editingMessage && repliedMessage && (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '8px 16px',
                        borderBottom: `1px solid ${token.colorBorderSecondary}`,
                    }}
                >
                    <div
                        style={{
                            flex: 1,
                            borderLeft: `3px solid ${token.colorPrimary}`,
                            paddingLeft: 8,
                        }}
                    >
                        <Text strong style={{ fontSize: 12, color: token.colorPrimary, display: 'block' }}>
                            {repliedSender ? `${repliedSender.firstName} ${repliedSender.lastName}` : 'Невідомий'}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {replyPreview}
                        </Text>
                    </div>
                    <Button
                        type="text"
                        size="small"
                        icon={<CloseOutlined />}
                        onClick={() => uiStore.clearReply()}
                        style={{ color: token.colorTextSecondary }}
                    />
                </div>
            )}

            {/* Attachment preview bar */}
            {!editingMessage && pending && (
                <div
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        padding: '8px 16px',
                        borderBottom: `1px solid ${token.colorBorderSecondary}`,
                    }}
                >
                    {pending.type === 'photo' ? (
                        <img
                            src={pending.objectUrl}
                            alt=""
                            style={{ width: 36, height: 36, borderRadius: 6, objectFit: 'cover' }}
                        />
                    ) : pending.type === 'video' ? (
                        <div
                            style={{
                                width: 36,
                                height: 36,
                                borderRadius: 6,
                                background: token.colorFillTertiary,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <VideoCameraOutlined style={{ fontSize: 18, color: token.colorPrimary }} />
                        </div>
                    ) : (
                        <div
                            style={{
                                width: 36,
                                height: 36,
                                borderRadius: 6,
                                background: token.colorPrimaryBg,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                            }}
                        >
                            <FileOutlined style={{ fontSize: 18, color: token.colorPrimary }} />
                        </div>
                    )}
                    <div style={{ flex: 1, minWidth: 0 }}>
                        <Text
                            style={{
                                fontSize: 13,
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                            }}
                        >
                            {pending.file.name}
                        </Text>
                    </div>
                    <Button
                        type="text"
                        size="small"
                        icon={<CloseOutlined />}
                        onClick={() => uiStore.clearPendingAttachment()}
                        style={{ color: token.colorTextSecondary }}
                    />
                </div>
            )}

            {/* Input row */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: 8,
                    padding: '8px 12px',
                }}
            >
                {CHAT_ATTACHMENTS_ENABLED && (
                    <Dropdown
                        menu={{
                            items: attachMenuItems,
                            onClick: ({ key }) => openFilePicker(key as 'photo' | 'video' | 'file'),
                        }}
                        trigger={['click']}
                        placement="topLeft"
                    >
                        <Button
                            type="text"
                            icon={<PaperClipOutlined style={{ fontSize: 18 }} />}
                            style={{ color: token.colorTextSecondary }}
                        />
                    </Dropdown>
                )}

                {uiStore.isMobile ? (
                    <Button
                        type="text"
                        icon={<SmileOutlined style={{ fontSize: 18 }} />}
                        style={{ color: isEmojiOpen ? token.colorPrimary : token.colorTextSecondary }}
                        onClick={() => setIsEmojiOpen((v) => !v)}
                    />
                ) : (
                    <EmojiPicker onSelect={handleEmojiSelect} />
                )}

                <TiptapEditor
                    onSend={handleSend}
                    onCancel={handleCancel}
                    onContentChange={handleContentChange}
                    initialContent={editInitialContent}
                    participants={mentionableUsers}
                    editorRef={editorRef}
                    isDark={uiStore.isDarkMode}
                    primaryColor={uiStore.primaryColor}
                />

                <Button
                    type="text"
                    icon={<SendOutlined style={{ fontSize: 18 }} />}
                    onClick={handleSend}
                    disabled={!canSend}
                    style={{
                        color: canSend ? token.colorPrimary : token.colorTextQuaternary,
                    }}
                />
            </div>

            {/* Inline emoji panel (mobile) */}
            {uiStore.isMobile && isEmojiOpen && (
                <div style={{ borderTop: `1px solid ${token.colorBorderSecondary}`, padding: '8px 12px' }}>
                    <EmojiPanel onSelect={handleEmojiSelect} columns={8} emojiSize={26} tabSize={22} gridHeight={220} />
                </div>
            )}

            {/* Hidden file input for attachments */}
            <input
                ref={fileInputRef}
                type="file"
                style={{ display: 'none' }}
                onChange={(e) => void handleFileSelected(e)}
            />
        </div>
    )
})

export default MessageInput
